import type { DataRequestV2 } from '~/common/types';
import type { UtilityAgency } from '.';

/**
 * CG-1219: rows for agencies that back a data request but are no longer in the project's agency
 * list. A merge moves the merged-away agency's geometry to the target, so the next recompute of
 * `projectAgencies` truncates its link row and the request disappears from the Utilities tab —
 * the request itself still exists. Unioning the two lists here keeps it visible.
 *
 * The request payload carries only the agency's identity (`getDataRequestsV2` selects id,
 * agencyId, agencyName, type), so every projectAgencies-derived field gets an explicit default:
 * the table cells and the progress counters read these without optional chaining.
 */
export function synthesizeRequestOnlyAgencies(
  linkedAgencies: UtilityAgency[],
  dataRequests: DataRequestV2[],
): { [agencyId: string]: UtilityAgency } {
  const linkedIds = new Set(linkedAgencies.map((a) => a.id));
  const rows: { [agencyId: string]: UtilityAgency } = {};

  for (const request of dataRequests) {
    const agency = request.agency;
    if (!agency?.id || linkedIds.has(agency.id) || rows[agency.id]) continue;

    rows[agency.id] = {
      ...agency,
      isRequestOnly: true,
      contacts: [],
      links: [],
      utilityNotes: null,
      responseStats: [],
      refreshCadence: 'Quarterly',
      assetsInProjectArea: false,
      availableDigitally: false,
      isPublished: false,
      isDigitized: false,
      // Keeps the row out of the Select Assets picker: the agency's boundary no longer covers this
      // project, so only its existing requests belong here, not new ones.
      dataRequestable: false,
      asbuiltsAvailable: false,
    } as unknown as UtilityAgency;
  }

  return rows;
}

/**
 * CG-1219: whether a Maps / As-builts cell must show an unavailable state instead of an action.
 * The Select Assets picker drops request-only rows, so offering "Request" on one dead-ends.
 */
export function blocksNewRequest(row: Pick<UtilityAgency, 'isRequestOnly'>, request: unknown): boolean {
  return !request && row.isRequestOnly === true;
}

/**
 * CG-1219: apply the flag to an agency map in place — merge the request-only rows in, or take
 * back out the ones a previous merge added. Flags can resolve either side of the fetch, so this
 * runs on both.
 */
export function mergeRequestOnlyAgencies(
  agencies: { [agencyId: string]: UtilityAgency },
  requestOnly: { [agencyId: string]: UtilityAgency },
  enabled: boolean,
) {
  for (const [id, agency] of Object.entries(requestOnly)) {
    if (enabled) {
      if (!agencies[id]) agencies[id] = agency;
    } else if (agencies[id]?.isRequestOnly) {
      delete agencies[id];
    }
  }
}
